import type { HTMLAttributes } from "react";

import React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

const MOBILE_CONFIG = {
  BASE_SCREEN_WIDTH: 393,
  BASE_HEADER_H: 59,
  BASE_ISLAND_W: 85,
  BASE_ISLAND_H: 25,
  BASE_ISLAND_MARGIN_TOP: 11,
  BASE_BOTTOM_H: 34,
  FRAME_BORDER_RADIUS: 55,
  BEZEL_BORDER_RADIUS: 50,
  SCREEN_BORDER_RADIUS: 36,
} as const;

export interface MobileProps extends HTMLAttributes<HTMLDivElement> {
  layoutClassName?: string;
  UNSAFE_className?: string;
  children?: React.ReactNode;
  frameClassName?: string;
  bezelClassName?: string;
  /** Scale factor for frame UI based on screen logical width (base 390). */
  frameScale?: number;
  /** iPhone style for the top cutout */
  variant?: "island" | "notch" | "legacy";
  /** Show/hide top safe area */
  hideStatusBar?: boolean;
  /** Show/hide bottom swipe bar area */
  hideNavBar?: boolean;
}

export const Mobile = React.forwardRef<HTMLDivElement, MobileProps>(
  (
    {
      layoutClassName,
      UNSAFE_className,
      className,
      children,
      frameClassName,
      bezelClassName,
      frameScale,
      variant,
      hideStatusBar,
      hideNavBar,
      ...props
    },
    ref,
  ) => {
    const FRAME_BG = frameClassName ?? "bg-[var(--color-border)]";
    const BEZEL_BG = bezelClassName ?? "bg-surface";
    const scale = frameScale ?? 1;

    const scaledDimensions = React.useMemo(() => {
      const BASE_SCREEN_HEIGHT = (MOBILE_CONFIG.BASE_SCREEN_WIDTH / 9) * 19.5; // ~845
      return {
        headerH: hideStatusBar ? 0 : MOBILE_CONFIG.BASE_HEADER_H * scale,
        bottomH: hideNavBar ? 0 : MOBILE_CONFIG.BASE_BOTTOM_H * scale,
        baseTotalHeight: BASE_SCREEN_HEIGHT,
        frameBorderRadius: MOBILE_CONFIG.FRAME_BORDER_RADIUS * scale,
        bezelBorderRadius: MOBILE_CONFIG.BEZEL_BORDER_RADIUS * scale,
        screenBorderRadius: MOBILE_CONFIG.SCREEN_BORDER_RADIUS * scale,
        islandStyle: {
          width: MOBILE_CONFIG.BASE_ISLAND_W * scale,
          height: MOBILE_CONFIG.BASE_ISLAND_H * scale,
          marginTop: MOBILE_CONFIG.BASE_ISLAND_MARGIN_TOP * scale,
          borderRadius: 50 * scale,
        },
        homeBarStyle: {
          width: Math.max(60, MOBILE_CONFIG.BASE_SCREEN_WIDTH * 0.35 * scale),
          height: 7 * scale,
          borderRadius: 100 * scale,
          marginBottom: 10 * scale,
        },
      };
    }, [scale, hideStatusBar, hideNavBar]);

    return (
      <div
        ref={ref}
        className={twMerge(
          "relative",
          layoutClassName,
          UNSAFE_className,
          className,
        )}
        {...props}
      >
        {/* Maintain device aspect ratio */}
        <div
          className="relative w-full"
          style={{
            aspectRatio: `${MOBILE_CONFIG.BASE_SCREEN_WIDTH} / ${scaledDimensions.baseTotalHeight}`,
          }}
        >
          <div className="absolute inset-0">
            {/* Frame */}
            <div
              className={twMerge(
                "relative w-full h-full p-1 overflow-hidden",
                FRAME_BG,
              )}
              style={{
                borderRadius: `${scaledDimensions.frameBorderRadius}px`,
              }}
            >
              {/* Bezel */}
              <div
                className={twMerge("w-full h-full p-1", BEZEL_BG)}
                style={{
                  borderRadius: `${scaledDimensions.bezelBorderRadius}px`,
                }}
              >
                {/* Top safe area/header (scaled) */}
                <div
                  className="relative w-full"
                  style={{ height: scaledDimensions.headerH }}
                >
                  {!hideStatusBar && (
                    <div
                      className="absolute left-1/2 -translate-x-1/2"
                      style={{
                        ...scaledDimensions.islandStyle,
                        backgroundColor: "var(--color-border)",
                      }}
                    />
                  )}
                </div>

                {/* Screen */}
                <div
                  className="w-full overflow-hidden bg-white"
                  style={{
                    height: `calc(100% - ${scaledDimensions.headerH + scaledDimensions.bottomH}px)`,
                    borderTopLeftRadius: hideStatusBar
                      ? `${scaledDimensions.screenBorderRadius}px`
                      : 0,
                    borderTopRightRadius: hideStatusBar
                      ? `${scaledDimensions.screenBorderRadius}px`
                      : 0,
                    borderBottomLeftRadius: hideNavBar
                      ? `${scaledDimensions.screenBorderRadius}px`
                      : 0,
                    borderBottomRightRadius: hideNavBar
                      ? `${scaledDimensions.screenBorderRadius}px`
                      : 0,
                  }}
                >
                  {children}
                </div>

                {/* Bottom swipe area */}
                {!hideNavBar && (
                  <div
                    className="w-full flex items-end justify-center"
                    style={{ height: scaledDimensions.bottomH }}
                  >
                    <div
                      style={{
                        ...scaledDimensions.homeBarStyle,
                        backgroundColor: "var(--color-border)",
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

Mobile.displayName = "Mobile";
